
import { FiArrowLeft, FiAward, FiBarChart2, FiPieChart, FiTrendingUp, FiUser } from 'react-icons/fi';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

function UserPerformanceDetails({ user, onBack }) {
  const { user: userData, responses } = user || {};

  // Prepare data for charts
  const scenarioPerformance = userData?.completedScenarios?.map(scenario => ({
    scenario: scenario.scenarioData?.title || `Scenario ${scenario.scenarioId}`,
    score: scenario.score,
    totalQuestions: scenario.totalQuestions,
    correctAnswers: scenario.correctAnswers
  })) || [];

  // Bar chart data
  const barChartData = {
    labels: scenarioPerformance.map(item => item.scenario),
    datasets: [
      {
        label: 'Score (%)',
        data: scenarioPerformance.map(item => item.score),
        backgroundColor: 'rgba(99, 102, 241, 0.8)',
        borderColor: 'rgba(99, 102, 241, 1)',
        borderWidth: 1,
      },
    ],
  };

  // Pie chart data
  const pieChartData = {
    labels: ['Completed', 'Remaining'],
    datasets: [
      {
        data: [
          scenarioPerformance.length,
          Math.max(0, 3 - scenarioPerformance.length) // Assuming 3 total scenarios
        ],
        backgroundColor: [
          'rgba(99, 102, 241, 0.8)',
          'rgba(200, 200, 200, 0.3)'
        ],
        borderColor: [
          'rgba(99, 102, 241, 1)',
          'rgba(200, 200, 200, 1)'
        ],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-purple-600 hover:text-purple-800 font-medium"
        >
          <FiArrowLeft /> Back to Users
        </button>
        <h2 className="text-2xl font-bold text-gray-800">User Performance</h2>
      </div>

      {/* User Info Card */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
              <FiUser className="text-2xl text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-800">User</h3>
            <p className="text-gray-600">{userData?.email}</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
              <FiAward className="text-2xl text-yellow-600" />
            </div>
            <h3 className="font-semibold text-gray-800">Points</h3>
            <p className="text-2xl font-bold text-yellow-700">{userData?.points || 0}</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
              <FiTrendingUp className="text-2xl text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-800">Scenarios</h3>
            <p className="text-2xl font-bold text-green-700">
              {userData?.completedScenarios?.length || 0}
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
              <FiBarChart2 className="text-2xl text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-800">Avg Score</h3>
            <p className="text-2xl font-bold text-blue-700">
              {scenarioPerformance.length > 0 
                ? Math.round(scenarioPerformance.reduce((sum, item) => sum + item.score, 0) / scenarioPerformance.length) 
                : 0
              }%
            </p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <FiBarChart2 className="text-purple-600" />
            <h3 className="text-lg font-semibold">Performance by Scenario</h3>
          </div>
          <div className="h-80">
            <Bar data={barChartData} options={chartOptions} />
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <FiPieChart className="text-purple-600" />
            <h3 className="text-lg font-semibold">Completion Progress</h3>
          </div>
          <div className="h-80">
            <Pie data={pieChartData} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* Detailed Scenario Performance */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Scenario Details</h3>
        <div className="overflow-x-auto">
          <table className="w-full table-auto">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-3 font-semibold text-gray-700">Scenario</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Score</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Correct</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Total</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Date</th>
              </tr>
            </thead>
            <tbody>
              {scenarioPerformance.map((scenario, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {scenario.scenario}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 py-1 px-2 rounded-full text-sm ${
                      scenario.score >= 80 ? 'bg-green-100 text-green-800' :
                      scenario.score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {scenario.score}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {scenario.correctAnswers}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {scenario.totalQuestions}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-sm">
                    {new Date().toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {scenarioPerformance.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No scenario data available</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserPerformanceDetails;